// src/pages/AdminDocuments.tsx

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { collection, getDocs, getDoc, doc, query, orderBy } from 'firebase/firestore';
import { useUserRole } from '../hooks/useUserRole';
import Navigation from '../components/Navigation';
import { logDataAccess } from '../lib/audit/auditLogger';

interface TaskAttachment {
  id: string;
  url: string;
  fileName: string;
  fileSize: number;
  uploadedBy: string;
  uploadedByName?: string;
  uploadedAt: any;
}

interface Task {
  id: string;
  title: string;
  description?: string;
  clientId: string;
  clientName?: string;
  clientEmail?: string;
  attachments?: TaskAttachment[];
  createdAt?: any;
  updatedAt?: any;
}

interface DocumentWithTask {
  attachment: TaskAttachment;
  taskId: string;
  taskTitle: string;
  clientId: string;
  clientName: string;
  clientEmail?: string;
}

type SortOption = 'date-desc' | 'date-asc' | 'name-asc' | 'name-desc' | 'size-desc' | 'size-asc' | 'task-asc' | 'task-desc' | 'client-asc' | 'client-desc';

const AdminDocuments: React.FC = () => {
  const { user, role, loading: roleLoading } = useUserRole();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState<DocumentWithTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('date-desc');
  const [filterByClient, setFilterByClient] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const loadDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const tasksRef = collection(db, 'tasks');
      const tasksQuery = query(tasksRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(tasksQuery);
      
      const allDocuments: DocumentWithTask[] = [];
      
      // Helper function to get client name
      const getClientName = async (clientId: string): Promise<string> => {
        try {
          // First try to get from users collection
          const userDocRef = doc(db, 'users', clientId);
          const userDocSnap = await getDoc(userDocRef);
          if (userDocSnap.exists()) {
            const userData = userDocSnap.data();
            if (userData.displayName) {
              return userData.displayName;
            } else if (userData.firstName && userData.lastName) {
              return `${userData.firstName} ${userData.lastName}`;
            } else if (userData.email) {
              return userData.email;
            }
          }
          
          // Try clients collection
          const clientDocRef = doc(db, 'clients', clientId);
          const clientDocSnap = await getDoc(clientDocRef);
          if (clientDocSnap.exists()) {
            const clientData = clientDocSnap.data();
            if (clientData.fullName) {
              return clientData.fullName;
            } else if (clientData.firstName && clientData.lastName) {
              return `${clientData.firstName} ${clientData.lastName}`;
            } else if (clientData.businessName) {
              return clientData.businessName;
            } else if (clientData.email) {
              return clientData.email;
            }
          }
        } catch (err) {
          console.warn('Could not load client info:', err);
        }
        return 'Unknown Client';
      };

      const getClientEmail = async (clientId: string): Promise<string | undefined> => {
        try {
          const userDocRef = doc(db, 'users', clientId);
          const userDocSnap = await getDoc(userDocRef);
          if (userDocSnap.exists()) {
            const userData = userDocSnap.data();
            if (userData.email) {
              return userData.email;
            }
          }
          
          const clientDocRef = doc(db, 'clients', clientId);
          const clientDocSnap = await getDoc(clientDocRef);
          if (clientDocSnap.exists()) {
            const clientData = clientDocSnap.data();
            if (clientData.email) {
              return clientData.email;
            }
          }
        } catch (err) {
          console.warn('Could not load client email:', err);
        }
        return undefined;
      };
      
      // Process all tasks and extract attachments
      for (const docSnap of snapshot.docs) {
        const taskData = docSnap.data() as Task;
        const taskId = docSnap.id;
        const taskTitle = taskData.title || 'Untitled Task';
        const clientId = taskData.clientId;
        
        // Get client name and email
        const clientName = await getClientName(clientId);
        const clientEmail = await getClientEmail(clientId);
        
        // Extract all attachments from this task
        if (taskData.attachments && taskData.attachments.length > 0) {
          taskData.attachments.forEach((attachment) => {
            allDocuments.push({
              attachment,
              taskId,
              taskTitle,
              clientId,
              clientName,
              clientEmail
            });
          });
        }
      }
      
      // Initial sort by upload date (newest first)
      allDocuments.sort((a, b) => {
        const aDate = a.attachment.uploadedAt?.toDate 
          ? a.attachment.uploadedAt.toDate() 
          : (a.attachment.uploadedAt ? new Date(a.attachment.uploadedAt) : new Date(0));
        const bDate = b.attachment.uploadedAt?.toDate 
          ? b.attachment.uploadedAt.toDate() 
          : (b.attachment.uploadedAt ? new Date(b.attachment.uploadedAt) : new Date(0));
        return bDate.getTime() - aDate.getTime();
      });
      
      setDocuments(allDocuments);
    } catch (error: any) {
      console.error('Error loading documents:', error);
      setError('Failed to load documents: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!roleLoading && role !== 'admin') {
      navigate('/client-dashboard');
    }
  }, [roleLoading, role, navigate]);

  useEffect(() => {
    if (role === 'admin') {
      loadDocuments();
    }
  }, [role, loadDocuments]);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (date: any): string => {
    if (!date) return 'Unknown date';
    const dateObj = date?.toDate ? date.toDate() : new Date(date);
    if (isNaN(dateObj.getTime())) return 'Unknown date';
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get unique clients for filter dropdown
  const uniqueClients = useMemo(() => {
    const clientMap = new Map<string, { id: string; name: string; email?: string }>();
    documents.forEach(doc => {
      if (!clientMap.has(doc.clientId)) {
        clientMap.set(doc.clientId, {
          id: doc.clientId,
          name: doc.clientName,
          email: doc.clientEmail
        });
      }
    });
    return Array.from(clientMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [documents]);

  // Reset to page 1 when filters or sort change
  useEffect(() => {
    setCurrentPage(1);
  }, [sortBy, filterByClient]);

  // Sort and filter documents
  const sortedAndFilteredDocuments = useMemo(() => {
    let filtered = documents;
    
    // Apply client filter
    if (filterByClient !== 'all') {
      filtered = documents.filter(doc => doc.clientId === filterByClient);
    }
    
    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'date-desc':
          const aDateDesc = a.attachment.uploadedAt?.toDate 
            ? a.attachment.uploadedAt.toDate() 
            : (a.attachment.uploadedAt ? new Date(a.attachment.uploadedAt) : new Date(0));
          const bDateDesc = b.attachment.uploadedAt?.toDate 
            ? b.attachment.uploadedAt.toDate() 
            : (b.attachment.uploadedAt ? new Date(b.attachment.uploadedAt) : new Date(0));
          return bDateDesc.getTime() - aDateDesc.getTime();
        
        case 'date-asc':
          const aDateAsc = a.attachment.uploadedAt?.toDate 
            ? a.attachment.uploadedAt.toDate() 
            : (a.attachment.uploadedAt ? new Date(a.attachment.uploadedAt) : new Date(0));
          const bDateAsc = b.attachment.uploadedAt?.toDate 
            ? b.attachment.uploadedAt.toDate() 
            : (b.attachment.uploadedAt ? new Date(b.attachment.uploadedAt) : new Date(0));
          return aDateAsc.getTime() - bDateAsc.getTime();
        
        case 'name-asc':
          return a.attachment.fileName.localeCompare(b.attachment.fileName);
        
        case 'name-desc':
          return b.attachment.fileName.localeCompare(a.attachment.fileName);
        
        case 'size-desc':
          return b.attachment.fileSize - a.attachment.fileSize;
        
        case 'size-asc':
          return a.attachment.fileSize - b.attachment.fileSize;
        
        case 'task-asc':
          return a.taskTitle.localeCompare(b.taskTitle);
        
        case 'task-desc':
          return b.taskTitle.localeCompare(a.taskTitle);
        
        case 'client-asc':
          return a.clientName.localeCompare(b.clientName);
        
        case 'client-desc':
          return b.clientName.localeCompare(a.clientName);
        
        default:
          return 0;
      }
    });
    
    return sorted;
  }, [documents, sortBy, filterByClient]);

  // Pagination calculations
  const totalPages = Math.ceil(sortedAndFilteredDocuments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedDocuments = sortedAndFilteredDocuments.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (roleLoading || loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (role !== 'admin') {
    return null;
  }

  return (
    <div>
      <Navigation />
      <div style={{ padding: '2rem', maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <h1>Admin Documents</h1>
          <button
            onClick={() => navigate('/admin-dashboard')}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            ← Back to Dashboard
          </button>
        </div>

        {error && (
          <div style={{
            padding: '1rem',
            backgroundColor: '#f8d7da',
            color: '#721c24',
            borderRadius: '4px',
            marginBottom: '1rem'
          }}>
            {error}
          </div>
        )}

        {/* Sort and Filter Controls */}
        {documents.length > 0 && (
          <div style={{
            backgroundColor: '#f8f9fa',
            padding: '1.5rem',
            borderRadius: '8px',
            marginBottom: '2rem',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '1rem'
          }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#495057' }}>
                Sort By
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortOption)}
                style={{
                  padding: '0.5rem',
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  width: '100%'
                }}
              >
                <option value="date-desc">Date (Newest First)</option>
                <option value="date-asc">Date (Oldest First)</option>
                <option value="name-asc">File Name (A-Z)</option>
                <option value="name-desc">File Name (Z-A)</option>
                <option value="size-desc">File Size (Largest First)</option>
                <option value="size-asc">File Size (Smallest First)</option>
                <option value="task-asc">Task Name (A-Z)</option>
                <option value="task-desc">Task Name (Z-A)</option>
                <option value="client-asc">Client Name (A-Z)</option>
                <option value="client-desc">Client Name (Z-A)</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#495057' }}>
                Filter by Client
              </label>
              <select
                value={filterByClient}
                onChange={(e) => setFilterByClient(e.target.value)}
                style={{
                  padding: '0.5rem',
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  width: '100%'
                }}
              >
                <option value="all">All Clients</option>
                {uniqueClients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {documents.length === 0 && !loading && (
          <div style={{
            padding: '3rem',
            textAlign: 'center',
            backgroundColor: '#f8f9fa',
            borderRadius: '4px',
            color: '#6c757d'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📄</div>
            <h3 style={{ marginBottom: '0.5rem', color: '#495057' }}>No documents found</h3>
            <p>Documents attached to tasks will appear here.</p>
          </div>
        )}

        {paginatedDocuments.length > 0 && (
          <>
            <div style={{
              display: 'grid',
              gap: '1rem',
              marginBottom: '2rem'
            }}>
              {paginatedDocuments.map((docWithTask) => {
              const { attachment, taskTitle, clientName } = docWithTask;
              return (
                <div
                  key={`${docWithTask.taskId}-${attachment.id}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '1.5rem',
                    backgroundColor: '#ffffff',
                    border: '1px solid #dee2e6',
                    borderRadius: '8px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    transition: 'box-shadow 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '1rem',
                      marginBottom: '0.5rem'
                    }}>
                      <div style={{ fontSize: '2rem' }}>📎</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <h3 style={{ 
                          margin: 0, 
                          marginBottom: '0.25rem',
                          color: '#212529',
                          fontSize: '1.1rem',
                          wordBreak: 'break-word'
                        }}>
                          {attachment.fileName}
                        </h3>
                        <div style={{ 
                          color: '#6c757d', 
                          fontSize: '0.875rem',
                          marginBottom: '0.5rem'
                        }}>
                          <strong>Client:</strong> {clientName}
                        </div>
                        <div style={{ 
                          color: '#6c757d', 
                          fontSize: '0.875rem',
                          marginBottom: '0.5rem'
                        }}>
                          <strong>Task:</strong> {taskTitle}
                        </div>
                        <div style={{ 
                          display: 'flex', 
                          gap: '1rem',
                          flexWrap: 'wrap',
                          color: '#6c757d',
                          fontSize: '0.875rem'
                        }}>
                          <span><strong>Size:</strong> {formatFileSize(attachment.fileSize)}</span>
                          <span><strong>Uploaded:</strong> {formatDate(attachment.uploadedAt)}</span>
                          {attachment.uploadedByName && (
                            <span><strong>By:</strong> {attachment.uploadedByName}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div style={{ marginLeft: '1rem' }}>
                    <a
                      href={attachment.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={async () => {
                        // Log document access
                        if (user && user.email && role) {
                          logDataAccess(
                            user.uid,
                            user.email,
                            role as 'admin' | 'staff' | 'client',
                            'document',
                            attachment.id,
                            `Document accessed: ${attachment.fileName} (Task: ${taskTitle}, Client: ${clientName})`,
                            false
                          ).catch((err: any) => console.error('Failed to log document access:', err));
                        }
                      }}
                      style={{
                        padding: '0.75rem 1.5rem',
                        backgroundColor: '#007bff',
                        color: 'white',
                        textDecoration: 'none',
                        borderRadius: '4px',
                        fontWeight: '500',
                        display: 'inline-block',
                        transition: 'background-color 0.2s ease',
                        whiteSpace: 'nowrap'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#0056b3';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#007bff';
                      }}
                    >
                      View Document
                    </a>
                  </div>
                </div>
              );
            })}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '0.5rem',
                marginTop: '2rem',
                flexWrap: 'wrap'
              }}>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: currentPage === 1 ? '#e9ecef' : '#007bff',
                    color: currentPage === 1 ? '#6c757d' : 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: '500'
                  }}
                >
                  Previous
                </button>

                {/* Page Numbers */}
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                  // Show first page, last page, current page, and pages around current
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        style={{
                          padding: '0.5rem 1rem',
                          backgroundColor: currentPage === page ? '#007bff' : 'white',
                          color: currentPage === page ? 'white' : '#007bff',
                          border: '1px solid #007bff',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          minWidth: '2.5rem'
                        }}
                      >
                        {page}
                      </button>
                    );
                  } else if (
                    page === currentPage - 2 ||
                    page === currentPage + 2
                  ) {
                    return (
                      <span key={page} style={{ padding: '0 0.25rem', color: '#6c757d' }}>
                        ...
                      </span>
                    );
                  }
                  return null;
                })}

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: currentPage === totalPages ? '#e9ecef' : '#007bff',
                    color: currentPage === totalPages ? '#6c757d' : 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: '500'
                  }}
                >
                  Next
                </button>
              </div>
            )}

            {/* Pagination Info */}
            {sortedAndFilteredDocuments.length > 0 && (
              <div style={{
                textAlign: 'center',
                color: '#6c757d',
                fontSize: '0.875rem',
                marginTop: '1rem'
              }}>
                Showing {startIndex + 1} to {Math.min(endIndex, sortedAndFilteredDocuments.length)} of {sortedAndFilteredDocuments.length} documents
              </div>
            )}
          </>
        )}

        {documents.length > 0 && sortedAndFilteredDocuments.length === 0 && (
          <div style={{
            padding: '3rem',
            textAlign: 'center',
            backgroundColor: '#f8f9fa',
            borderRadius: '4px',
            color: '#6c757d'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔍</div>
            <h3 style={{ marginBottom: '0.5rem', color: '#495057' }}>No documents match your filter</h3>
            <p>Try adjusting your filter criteria to see more documents.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDocuments;

